import {
  Student,
  EmbeddedAccreditation,
  StatusHistoryEntry,
} from '../interfaces/student.interfaces';

export interface BackendStudent {
  _id?: string;
  authId?: string;
  folio?: string;

  nombreCompleto: string;
  matricula: string;
  curp?: string;

  correoPersonal?: string;
  correoInstitucional: string;

  escuela: string;
  carrera: string;

  fechaArranque: string | Date;
  
  fechaConclusion?: string | Date;

  nombreAuditor: string;
  telefono: string;
  hasWhatsapp?: boolean;
  whatsappNumber?: string;
  hasSecondaryContact?: boolean;
  secondaryPhone?: string;
  comoSupisteDeAbaq?: string;

  fechaNacimiento: string | Date;

  horasCubrir: number;
  horasAcreditadas: number;
  horasPendientes: number;

  semestre?: number | string;
  periodType?: string;
  periodNumber?: number | string;

  cartaAceptacion: string;
  cartaTermino: string;
  constanciaPlatica: string;
  reporteActividades: string;
  reporteFinal: string;

  facebookUsuario?: string;
  instagramUsuario?: string;
  tiktokUsuario?: string;
  xUsuario?: string;
  youtubeUsuario?: string;
  linkedinUsuario?: string;
  accountStatus?: 'pending' | 'active' | 'inactive' | 'rejected';
  bioPersonal?: string;
  intereses?: string;
  talentos?: string;
  aportesAbaq?: string;
  animalExperience?: string;
  descExperiencia?: string;
  motivacionAbaq?: string;
  rankedActivities?: string[];
  consents?: any;
  onboardingData?: any;
  cloudUrls?: {
    ine?: string;
    cv?: string;
    comprobante?: string;
  };
  fechaInicioFormularioExpediente?: string | Date;
  fechaFinFormularioExpediente?: string | Date;
  fechaEmisionExpediente?: string | Date;
  fechaAceptacionExpediente?: string | Date;
  fechaExpedicionExpediente?: string | Date;
  tiempoLlenadoMinutosExpediente?: number;

  assignedActivities?: Array<{
    activityRef?: string;
    title?: string;
    type?: string;
    comments?: string;
    dueDate?: string | Date;
    valueInHours?: number;
    validatedHours?: number;
    interactionFolio?: string;
    interactionType?: 'AC' | 'OP';
    status?:
      | 'registered'
      | 'attended'
      | 'absent'
      | 'validated'
      | 'cancelled';

    performance?:
      | 'pending'
      | 'very_bad'
      | 'bad'
      | 'good'
      | 'very_good'
      | 'excellent'
      | 'absent'
      | null;

    performanceComment?: string;
  }>;

  accreditations?: Array<{
    _id?: string;
    interactionFolio?: string;
    interactionType?: 'AC' | 'OP';
    type?: string;
    categoryCode?: string;
    status?: string;
    date?: string | Date;
    hoursAccredited?: number;
    hoursPerformed?: number;
    evidenceUrl?: string;
    comments?: string;
    performance?: string | null;
    performanceComment?: string;
  }>;

  statusHistory?: Array<{
    fromStatus?: string;
    toStatus?: string;
    reason?: string;
    comments?: string;
    changedByEmail?: string;
    date?: string | Date;
  }>;
}

interface ApiListResponse {
  status: string;
  results: number;
  data: {
    documents: BackendStudent[];
  };
}

interface ApiDocumentResponse {
  status: string;
  data: {
    document: BackendStudent;
  };
}

function toDate(
  value: string | Date | undefined,
): Date {
  if (!value) {
    return new Date();
  }

  return value instanceof Date
    ? value
    : new Date(value);
}

export function mapBackendStudentToFrontend(
  doc: BackendStudent,
): Student {
  return {
    id: doc._id ?? '',
    authId: doc.authId ?? '',
    folio: doc.folio || undefined,

    matricula: doc.matricula ?? '',
    curp: doc.curp ?? (doc as any).CURP ?? '',

    fullName: doc.nombreCompleto || doc.correoPersonal || doc.correoInstitucional || 'Estudiante',

    mail: doc.correoPersonal || doc.correoInstitucional || '',

    schoolMail: doc.correoInstitucional || doc.correoPersonal || '',

    phone: doc.telefono,
    hasWhatsapp: doc.hasWhatsapp ?? (doc as any).onboardingData?.hasWhatsapp,
    whatsappNumber: doc.whatsappNumber ?? (doc as any).onboardingData?.whatsappNumber,
    hasSecondaryContact: doc.hasSecondaryContact ?? (doc as any).onboardingData?.hasSecondaryContact,
    secondaryContactPhone:
      doc.secondaryPhone ??
      (doc as any).secondaryContactPhone ??
      (doc as any).onboardingData?.secondaryPhone,

    comoSupisteDeAbaq: doc.comoSupisteDeAbaq ?? '',

    career: doc.carrera,

    school: doc.escuela,

    auditorName: doc.nombreAuditor,

    birthDate: toDate(
      doc.fechaNacimiento,
    ),

    semester: doc.semestre ?? 1,
    periodType: doc.periodType,
    periodNumber: doc.periodNumber,

    horasCubrir:
      doc.horasCubrir ?? 0,

    horasAcreditadas:
      doc.horasAcreditadas ?? 0,

    horasPendientes:
      doc.horasPendientes ?? 0,

    cartaAceptacion:
      doc.cartaAceptacion ?? 'pending',

    cartaTermino:
      doc.cartaTermino ?? 'pending',

    constanciaPlatica:
      doc.constanciaPlatica ?? 'pending',

    reporteActividades:
      doc.reporteActividades ?? 'pending',

    reporteFinal: 
      doc.reporteFinal ?? 'pending',

    assignedActivities:
      (doc.assignedActivities ?? []).map(
        (activity) => ({
          activityRef:
            activity.activityRef?.toString() ?? '',

          title:
            activity.title,

          type:
            activity.type,

          comments:
            activity.comments,

          dueDate:
            toDate(activity.dueDate),

          valueInHours:
            activity.valueInHours,

          validatedHours:
            activity.validatedHours,

          interactionFolio:
            activity.interactionFolio,

          interactionType:
            activity.interactionType,

          status:
            activity.status ?? 'registered',
          
          performance:
            activity.performance ??
            'pending',
          
          performanceComment:
            activity.performanceComment ??
            '',
        }),
      ),

    accreditations:
      (doc.accreditations ?? []).map(
        (acc): EmbeddedAccreditation => ({
          _id: acc._id,
          interactionFolio:
            acc.interactionFolio ?? '',
          interactionType:
            acc.interactionType ?? 'AC',
          type:
            acc.type ?? '',
          categoryCode:
            acc.categoryCode,
          status:
            acc.status ?? '',
          date:
            acc.date ? toDate(acc.date) : undefined,
          hoursAccredited:
            acc.hoursAccredited,
          hoursPerformed:
            acc.hoursPerformed,
          evidenceUrl:
            acc.evidenceUrl,
          comments:
            acc.comments,
          performance:
            acc.performance,
          performanceComment:
            acc.performanceComment,
        }),
      ),

    statusHistory:
      (doc.statusHistory ?? []).map(
        (entry): StatusHistoryEntry => ({
          fromStatus:
            entry.fromStatus,
          toStatus:
            entry.toStatus,
          reason:
            entry.reason,
          comments:
            entry.comments,
          changedByEmail:
            entry.changedByEmail,
          date:
            entry.date ? toDate(entry.date) : undefined,
        }),
      ),

    periods: [
      {
        desiredHours:
          doc.horasCubrir ?? 0,

        validatedHours:
          doc.horasAcreditadas ?? 0,

        begin:
          toDate(doc.fechaArranque),

        end:
          toDate(
            doc.fechaConclusion ??
            doc.fechaArranque,
          ),
      },
    ],

    socialMedia: {
      facebook:
        doc.facebookUsuario ?? '',

      instagram:
        doc.instagramUsuario ?? '',

      tiktok:
        doc.tiktokUsuario ?? '',

      x:
        doc.xUsuario ?? '',

      youtube:
        doc.youtubeUsuario ?? '',

      linkedin:
        doc.linkedinUsuario ?? '',
    },

    created:
      toDate(doc.fechaArranque),

    fechaArranque:
      toDate(doc.fechaArranque),

    updated:
      toDate(
        doc.fechaConclusion ??
        doc.fechaArranque,
      ),

    accountStatus:
      doc.accountStatus ?? 'pending',

    bioPersonal: doc.bioPersonal || (doc as any).onboardingData?.bioPersonal || '',
    intereses: doc.intereses || (doc as any).onboardingData?.intereses || '',
    talentos: doc.talentos || (doc as any).onboardingData?.talentos || '',
    aportesAbaq: doc.aportesAbaq || (doc as any).onboardingData?.aportesAbaq || '',
    animalExperience: doc.animalExperience || (doc as any).onboardingData?.animalExperience || '',
    descExperiencia: doc.descExperiencia || (doc as any).onboardingData?.descExperiencia || '',
    motivacionAbaq: doc.motivacionAbaq || (doc as any).onboardingData?.motivacionAbaq || '',
    rankedActivities: doc.rankedActivities && doc.rankedActivities.length > 0 ? doc.rankedActivities : ((doc as any).onboardingData?.rankedActivities || []),
    consents: doc.consents || (doc as any).onboardingData?.consents || {
      reglamento: false,
      privacidad: false,
      etica: false,
      incognito: false,
      consentimiento: false,
    },
    onboardingData: (doc as any).onboardingData || {},

    cloudUrls: doc.cloudUrls
      ? {
          ine: doc.cloudUrls.ine || '',
          cv: doc.cloudUrls.cv || '',
          comprobante: doc.cloudUrls.comprobante || '',
        }
      : { ine: '', cv: '', comprobante: '' },

    fechaInicioFormularioExpediente: doc.fechaInicioFormularioExpediente ? toDate(doc.fechaInicioFormularioExpediente) : undefined,
    fechaFinFormularioExpediente: doc.fechaFinFormularioExpediente ? toDate(doc.fechaFinFormularioExpediente) : undefined,
    fechaEmisionExpediente: doc.fechaEmisionExpediente ? toDate(doc.fechaEmisionExpediente) : undefined,
    fechaAceptacionExpediente: doc.fechaAceptacionExpediente ? toDate(doc.fechaAceptacionExpediente) : undefined,
    fechaExpedicionExpediente: doc.fechaExpedicionExpediente ? toDate(doc.fechaExpedicionExpediente) : undefined,
    tiempoLlenadoMinutosExpediente: doc.tiempoLlenadoMinutosExpediente,
  };
}

export function mapFrontendStudentToBackend(
  student: Partial<Student>,
): Partial<BackendStudent> {
  const period =
    student.periods?.[0];

  const desiredHours =
    student.horasCubrir ??
    period?.desiredHours ??
    0;
  
  const validatedHours =
    student.horasAcreditadas ??
    period?.validatedHours ??
    0;

  return {
    authId:
      student.authId,

    nombreCompleto:
      student.fullName,

    matricula:
      student.matricula ?? '',

    correoPersonal:
      student.mail,

    correoInstitucional:
      student.schoolMail,

    escuela:
      student.school,

    carrera:
      student.career,

    nombreAuditor:
      student.auditorName,

    telefono:
      student.phone,

    comoSupisteDeAbaq:
      student.comoSupisteDeAbaq,
    
    fechaNacimiento:
      student.birthDate?.toISOString?.(),

    fechaArranque:
      period?.begin?.toISOString?.(),

    fechaConclusion:
      period?.end?.toISOString?.(),

    horasCubrir:
      desiredHours,

    horasAcreditadas:
      validatedHours,

    horasPendientes:
      Math.max(
        desiredHours - validatedHours,
        0,
      ),

    semestre:
      student.semester,

    cartaAceptacion:
      student.cartaAceptacion,

    cartaTermino:
      student.cartaTermino,

    constanciaPlatica:
      student.constanciaPlatica,
    
    reporteActividades:
      student.reporteActividades,

    facebookUsuario:
      student.socialMedia?.facebook,

    instagramUsuario:
      student.socialMedia?.instagram,

    tiktokUsuario:
      student.socialMedia?.tiktok,

    xUsuario:
      student.socialMedia?.x,

    youtubeUsuario:
      student.socialMedia?.youtube,

    linkedinUsuario:
      student.socialMedia?.linkedin,

    accountStatus:
      student.accountStatus,

    cloudUrls:
      student.cloudUrls,

    fechaInicioFormularioExpediente:
      student.fechaInicioFormularioExpediente,

    fechaFinFormularioExpediente:
      student.fechaFinFormularioExpediente,

    fechaEmisionExpediente:
      student.fechaEmisionExpediente,

    fechaAceptacionExpediente:
      student.fechaAceptacionExpediente,

    fechaExpedicionExpediente:
      student.fechaExpedicionExpediente,

    tiempoLlenadoMinutosExpediente:
      student.tiempoLlenadoMinutosExpediente,
  };
}

export type {
  ApiListResponse,
  ApiDocumentResponse,
};
